import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import {
  accountSearchColumns,
  hanThanhToanSearchColumns,
  lyDoNhapXuatSearchColumns,
  MA_CHUNG_TU,
  QUERY_KEYS,
  supplierSearchColumns
} from '@/constants';
import { AritoIcon, CurrencyInput, DocumentNumberField, FormField, SearchField } from '@/components/custom/erp';
import { AccountModel, HanThanhToan, KhachHang, NhapXuat } from '@/types/schemas';
import { usePhieuXuatTraLaiNhaCungCapContext } from '../../context';
import { useDetailSelection } from '@/hooks/use-detail-selection';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface BasicInfoTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

export default function BasicInfoTab({ formMode, formState: { state, actions } }: BasicInfoTabProps) {
  const { setValue } = useFormContext();
  const handleAccountSelection = useDetailSelection<AccountModel>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    onSelect: actions.setTaiKhoan
  });
  const ngay_ct = useWatch({ name: 'ngay_ct' });
  const { setNgayCt } = usePhieuXuatTraLaiNhaCungCapContext();

  useEffect(() => {
    if (ngay_ct) {
      setValue('ngay_ct0', ngay_ct);
    }
    setNgayCt(ngay_ct);
  }, [ngay_ct, setNgayCt, setValue]);

  return (
    <div className='flex flex-col gap-3 p-4'>
      {/* Customer Information Section */}
      <div className='grid grid-cols-12 gap-6'>
        {/* Left Column */}
        <div className='col-span-6 space-y-1'>
          <div className='flex items-center'>
            <Label className='w-[195px]'>Mã nhà cung cấp</Label>
            <SearchField<KhachHang>
              type='text'
              name='ma_kh'
              value={state.nhaCungCap?.customer_code || ''}
              searchEndpoint={`/${QUERY_KEYS.NHA_CUNG_CAP}/`}
              searchColumns={supplierSearchColumns}
              onRowSelection={actions.setNhaCungCap}
              columnDisplay='customer_code'
              dialogTitle='Danh mục nhà cung cấp'
              className='w-100'
              disabled={formMode === 'view'}
            />

            <div className='ml-4 flex items-center'>
              <Label className='w-20'>Mã số thuế</Label>
              <FormField
                name='ma_so_thue'
                type='text'
                disabled={formMode === 'view'}
                placeholder='Nhập và tra cứu'
                value={state.nhaCungCap?.tax_code || ''}
              />
              <button className='ml-2 flex items-center justify-center border border-gray-200 px-3 py-1.5'>
                <AritoIcon icon={15} className='shrink-0' />
              </button>
              <button className='ml-2 flex items-center justify-center border border-gray-200 px-3 py-1.5'>
                <AritoIcon icon={888} />
              </button>
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-[195px]'>Tên nhà cung cấp</Label>
            <div className='flex-1'>
              <FormField
                name='ten_kh_thue'
                type='text'
                disabled={formMode === 'view'}
                value={state.nhaCungCap?.customer_name || ''}
                placeholder='Nhập tên nhà cung cấp/đơn vị'
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-[195px]'>Địa chỉ</Label>
            <div className='flex-1'>
              <FormField
                name='dia_chi'
                type='text'
                disabled={formMode === 'view'}
                labelClassName='w-48'
                value={state.nhaCungCap?.address || ''}
                placeholder='Nhập địa chỉ nhà cung cấp'
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-[195px]'>Mã nhập xuất</Label>
            <SearchField<NhapXuat>
              type='text'
              name='ma_nx'
              value={state.nhapXuat?.ma_nx || ''}
              searchEndpoint={`/${QUERY_KEYS.LY_DO_NHAP_XUAT}/`}
              columnDisplay='ma_nx'
              displayRelatedField='ten_nx'
              searchColumns={lyDoNhapXuatSearchColumns}
              dialogTitle='Danh mục lý do nhập xuất'
              onRowSelection={actions.setNhapXuat}
              disabled={formMode === 'view'}
              className='w-100'
            />
          </div>

          <div className='flex items-center'>
            <Label className='w-[195px]'>Tài khoản nợ</Label>
            <SearchField<AccountModel>
              type='text'
              name='tk'
              value={state.taiKhoan?.code || state.nhaCungCap?.account_data?.code || ''}
              relatedFieldValue={state.nhaCungCap?.account_data?.name || state.taiKhoan?.name || ''}
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
              columnDisplay='code'
              displayRelatedField='name'
              searchColumns={accountSearchColumns}
              dialogTitle='Danh sách tài khoản'
              onRowSelection={handleAccountSelection}
              disabled={formMode === 'view'}
              className='w-100'
            />
          </div>

          <div className='flex items-center'>
            <Label className='w-[195px]'>Diễn giải</Label>
            <div className='flex-1'>
              <FormField name='dien_giai' disabled={formMode === 'view'} />
            </div>
          </div>
        </div>

        {/* Middle Column */}
        <div className='col-span-3 space-y-1'>
          <div className='ml-4 flex h-8 items-center'></div>

          <div className='ml-4 flex items-center'>
            <FormField
              name='ong_ba'
              label='Người nhận'
              labelClassName='w-32'
              inputClassName='w-6 text-red-600'
              disabled={formMode === 'view'}
              className='w-[250px]'
              value={state.nhaCungCap?.contact_person || ''}
              placeholder='Nhập tên người nhận'
            />
          </div>

          <div className='ml-4 flex items-center'>
            <FormField
              name='e_mail'
              label='Email'
              labelClassName='w-32'
              inputClassName='w-64'
              className='w-[250px]'
              disabled={formMode === 'view'}
              value={state.nhaCungCap?.email || ''}
              placeholder='Nhập email'
            />
          </div>

          <div className='ml-4 flex items-center'>
            <div className='flex items-center'>
              <Label className='w-[132px]'>Hạn thanh toán</Label>
              <SearchField<HanThanhToan>
                type='text'
                name='ma_tt'
                value={state.hanThanhToan?.ma_tt || state.nhaCungCap?.payment_term_data?.ma_tt || ''}
                searchEndpoint={`/${QUERY_KEYS.HAN_THANH_TOAN}/`}
                columnDisplay='ma_tt'
                searchColumns={hanThanhToanSearchColumns}
                dialogTitle='Danh mục hạn thanh toán'
                className='w-[230px]'
                onRowSelection={actions.setHanThanhToan}
                placeholder='Hạn thanh toán'
                disabled={formMode === 'view'}
              />
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className='col-span-3 flex'>
          {/* Tab Content */}
          <div className='flex-1 pr-2'>
            <DocumentNumberField
              ma_ct={MA_CHUNG_TU.MUA_HANG.PHIEU_XUAT_TRA_LAI_NHA_CUNG_CAP}
              quyenChungTu={state.quyenChungTu}
              onQuyenChungTuChange={actions.setQuyenChungTu}
              soChungTu={state.soChungTu}
              onSoChungTuChange={actions.setSoChungTu}
              disabled={formMode === 'view'}
              classNameSearchField='w-full'
              formMode={formMode}
            />

            <div className='flex items-center'>
              <Label className='w-32'>Ngày chứng từ</Label>
              <div className='flex-1'>
                <FormField name='ngay_ct' type='date' disabled={formMode === 'view'} labelClassName='w-32 shrink-0' />
              </div>
            </div>

            <div className='flex items-center'>
              <Label className='w-32'>Ngày hoá đơn</Label>
              <div className='flex-1'>
                <FormField name='ngay_ct0' type='date' disabled={formMode === 'view'} labelClassName='w-32 shrink-0' />
              </div>
            </div>

            <CurrencyInput formMode={formMode} classNameInput='flex-1' />

            <div className='flex items-center'>
              <Label className='w-32'>Trạng thái</Label>
              <div className='flex-1'>
                <FormField
                  name='status'
                  type='select'
                  disabled={formMode === 'view'}
                  options={[
                    { value: '0', label: 'Chưa ghi sổ' },
                    { value: '3', label: 'Chờ duyêt' },
                    { value: '5', label: 'Đã ghi sổ' },
                    { value: '9', label: 'Huỷ' }
                  ]}
                  defaultValue={'5'}
                />
              </div>
            </div>

            <div className='mt-1 flex'>
              <div className='mb-4 h-2 w-32 shrink-0' />
              <FormField
                label='Dữ liệu nhận được'
                name='transfer_yn'
                type='checkbox'
                disabled={true}
                labelClassName='w-32'
                defaultValue={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
